import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Ban,
  ChevronRight,
  ClipboardList,
  TrendingUp,
  UserCheck,
  ShieldCheck,
  Plus,
  Pencil,
} from '@/lib/icons';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ListFilterBar, NoFilterResults } from '@/components/filters';
import type { FilterChip } from '@/components/filters';
import { useListFilters } from '@/hooks/use-list-filters';
import { cn, formatCurrency } from '@/lib/utils';
import { useAppSelector } from '@/store/hooks';
import {
  getApplications,
  getApplicationCounts,
} from '@/features/applications/api';
import type { ApplicationStatusType } from '@/features/applications/application';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/dates';

const STATUS_VALUES = [
  'draft',
  'submitted',
  'pending_kyc',
  'approved',
  'rejected',
  'cancelled',
] as const;

const applicationsSearchSchema = z.object({
  q: z.string().optional(),
  branchId: z.string().optional(),
  status: z.enum(STATUS_VALUES).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_layout/applications/')({
  validateSearch: applicationsSearchSchema,
  component: ApplicationsPage,
});

const PAGE_SIZE = 20;

type StatusConfig = {
  label: string;
  className: string;
  icon: React.ReactNode;
};

const STATUS_CONFIG: Record<ApplicationStatusType, StatusConfig> = {
  draft: {
    label: 'Draft',
    className: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
    icon: <Pencil className="h-3 w-3" />,
  },
  submitted: {
    label: 'Submitted',
    className: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
    icon: <Clock className="h-3 w-3" />,
  },
  pending_kyc: {
    label: 'KYC Verified',
    className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    icon: <ShieldCheck className="h-3 w-3" />,
  },
  approved: {
    label: 'Approved',
    className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  rejected: {
    label: 'Rejected',
    className: 'bg-red-500/15 text-red-600 dark:text-red-400',
    icon: <XCircle className="h-3 w-3" />,
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-gray-500/15 text-gray-600 dark:text-gray-400',
    icon: <Ban className="h-3 w-3" />,
  },
};

function getStatusConfig(status: string): StatusConfig {
  return (
    STATUS_CONFIG[status as ApplicationStatusType] ?? {
      label: status,
      className: 'bg-gray-500/15 text-gray-600 dark:text-gray-400',
      icon: <FileText className="h-3 w-3" />,
    }
  );
}

const tierBadge: Record<string, string> = {
  A: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  B: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  C: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  D: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  E: 'bg-red-500/10 text-red-600 border-red-500/30',
};

type ChipValue = 'all' | (typeof STATUS_VALUES)[number];

function ApplicationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // `/applications/new` is route-guarded on applications:create — without it
  // the button just bounces to /forbidden.
  const canCreate = useAuth().hasPermission('applications', 'create');
  const search = Route.useSearch();
  const currency =
    useAppSelector((state) => state.tenant.settings?.currency) ?? 'USD';

  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearchPending,
    setFilters,
    reset,
    activeCount,
  } = useListFilters({ search, to: '/applications' });

  const page = search.page ?? 1;
  const statusFilter: ChipValue = search.status ?? 'all';

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [
      'applications',
      search.status,
      search.branchId,
      debouncedSearch,
      page,
    ],
    queryFn: () =>
      getApplications({
        status: search.status,
        branchId: search.branchId,
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
      }),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  });

  // Counts share the branch/search narrowing so a chip's number and the rows
  // it produces can never disagree.
  const { data: countsData } = useQuery({
    queryKey: ['applications-counts', search.branchId, debouncedSearch],
    queryFn: () =>
      getApplicationCounts({
        branchId: search.branchId,
        search: debouncedSearch,
      }),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  });

  const apps = data?.value?.items ?? [];
  const total = data?.value?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const statusCounts: Record<string, number> = countsData?.value?.counts ?? {
    all: 0,
  };

  const approvedCount = statusCounts.approved ?? 0;
  const allCount = statusCounts.all ?? 0;
  const approvalRate =
    allCount > 0 ? Math.round((approvedCount / allCount) * 100) : 0;

  const pipelineValue = statusCounts.pipelineValue ?? 0;
  const pendingReview =
    (statusCounts.submitted ?? 0) + (statusCounts.pending_kyc ?? 0);

  const stats = [
    {
      label: 'Total Applications',
      value: allCount,
      icon: ClipboardList,
      color: 'from-blue-500 to-blue-600',
    },
    {
      label: 'Pipeline Value',
      value: formatCurrency(pipelineValue, currency),
      icon: TrendingUp,
      color: 'from-emerald-500 to-emerald-600',
    },
    {
      label: 'Approval Rate',
      value: `${approvalRate}%`,
      icon: CheckCircle2,
      color: 'from-violet-500 to-violet-600',
    },
    {
      label: 'Pending Review',
      value: pendingReview,
      icon: UserCheck,
      color: 'from-amber-500 to-amber-600',
    },
  ];

  const chips: ReadonlyArray<FilterChip<ChipValue>> = [
    { value: 'all', label: 'All', count: statusCounts.all ?? 0 },
    ...STATUS_VALUES.map((s) => {
      const cfg = getStatusConfig(s);
      return {
        value: s,
        label: cfg.label,
        icon: cfg.icon,
        count: statusCounts[s] ?? 0,
      };
    }),
  ];

  const header = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Applications</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Review and process financing applications
        </p>
      </div>
      {canCreate && (
        <Button
          className="bg-gradient-to-r from-primary to-primary/85 text-primary-foreground shadow-md transition-all duration-200 hover:from-primary/90 hover:to-primary/75 hover:shadow-lg"
          onClick={() => navigate({ to: '/applications/new' })}
        >
          <Plus className="mr-2 h-4 w-4" />
          New Application
        </Button>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {header}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        {header}
        <ApiErrorCard
          error={error}
          onRetry={() => refetch()}
          title="Failed to load applications"
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      <div className="animate-stagger-in grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <Card
            key={s.label}
            className="relative overflow-hidden border-0 shadow-md"
          >
            <div
              className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-[0.07]`}
            />
            <CardContent className="relative flex items-center gap-3 p-4">
              <div
                className={`rounded-xl bg-gradient-to-br p-2.5 ${s.color} text-white shadow-lg`}
              >
                <s.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ListFilterBar
        chips={chips}
        chipValue={statusFilter}
        onChipChange={(value) =>
          setFilters({ status: value === 'all' ? undefined : value })
        }
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t('filters.searchPlaceholder.applications')}
        isSearchPending={isSearchPending}
        branchId={search.branchId}
        onBranchChange={(branchId) => setFilters({ branchId })}
        activeCount={activeCount}
        onReset={reset}
        isPending={isFetching}
      />

      {apps.length > 0 ? (
        <Card className="border-0 shadow-md">
          <CardContent className="p-0">
            <div className="divide-y">
              {apps.map((app) => {
                const cfg = getStatusConfig(app.status);
                const name = app.customerName ?? 'Unknown';
                const initials = name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();
                const colors = [
                  'bg-blue-500',
                  'bg-emerald-500',
                  'bg-violet-500',
                  'bg-amber-500',
                  'bg-rose-500',
                ];
                const idx = name.charCodeAt(0) % colors.length;
                return (
                  <div
                    key={app.id}
                    onClick={() =>
                      navigate({
                        to: '/applications/$applicationId',
                        params: { applicationId: app.id },
                      })
                    }
                    className="group cursor-pointer p-5 transition-colors hover:bg-muted/30"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`h-10 w-10 rounded-full ${colors[idx]} flex shrink-0 items-center justify-center text-xs font-bold text-white`}
                      >
                        {initials}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-foreground">
                              {name}
                            </h3>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {app.customerPhone ?? ''}
                            </p>
                          </div>
                          <Badge className={cn('shrink-0', cfg.className)}>
                            {cfg.icon}
                            <span className="ml-1">{cfg.label}</span>
                          </Badge>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(app.devicePrice, currency)}
                          </span>
                          {app.creditTier && (
                            <span
                              className={cn(
                                'inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold',
                                tierBadge[app.creditTier] ?? ''
                              )}
                            >
                              {app.creditTier}
                            </span>
                          )}
                          {app.creditScore != null && (
                            <span>Score: {app.creditScore}</span>
                          )}
                          {app.deviceBrand && app.deviceModel && (
                            <span className="max-w-40 truncate">
                              {app.deviceBrand} {app.deviceModel}
                            </span>
                          )}
                          {app.branchName && <span>{app.branchName}</span>}
                          <span>
                            {formatDate(app.createdAt)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <NoFilterResults
          isFiltered={activeCount > 0}
          emptyTitle="No applications yet"
          emptyDescription="Applications appear here once a customer applies for financing."
          icon={FileText}
          onReset={reset}
          action={
            canCreate ? (
              <Button onClick={() => navigate({ to: '/applications/new' })}>
                <Plus className="mr-2 h-4 w-4" />
                New Application
              </Button>
            ) : undefined
          }
        />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({total} total)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setFilters({ page: Math.max(1, page - 1) })}
              disabled={page <= 1}
              className="rounded-lg border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setFilters({ page: Math.min(totalPages, page + 1) })
              }
              disabled={page >= totalPages}
              className="rounded-lg border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
